import { useState, useCallback } from 'react';

const DEBUG = process.env.REACT_APP_DEBUG === 'true';

export const useChartData = (marketData, onLoadMoreData) => {
  const [allCandleData, setAllCandleData] = useState([]);
  const [allVolumeData, setAllVolumeData] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  const loadMoreThreshold = 50; // Load more data when scrolled within 50 candles of the start

  const processMarketData = useCallback((candleData) => {
    setAllCandleData(candleData);
    
    // Process volume data if available
    if (marketData?.candles?.[0]?.volume !== undefined) {
      const volumeData = marketData.candles.map((candle, index) => {
        const timestamp = candle.timestamp ? 
          new Date(candle.timestamp).getTime() / 1000 : 
          new Date(candle.time).getTime() / 1000;
        
        const candleItem = candleData[index];
        const color = candleItem && candleItem.close >= candleItem.open ? 
          'rgba(38, 166, 154, 0.5)' : 'rgba(239, 83, 80, 0.5)';
        
        return {
          time: timestamp,
          value: parseFloat(candle.volume || 0),
          color: color,
        };
      }).sort((a, b) => a.time - b.time);

      setAllVolumeData(volumeData);
    }
  }, [marketData]);

  const loadMoreHistoricalData = useCallback(async (timeRange) => {
    if (!timeRange || !onLoadMoreData || isLoadingMore || !allCandleData.length) {
      return;
    }

    const { from: visibleFrom } = timeRange;
    
    // Find the earliest data point we have
    const earliestDataTime = allCandleData[0]?.time;
    
    // Calculate how close the user is to the beginning of our data
    const timeDifference = earliestDataTime - visibleFrom;
    
    // Convert threshold from candle count to time (approximation)
    const averageCandleTime = allCandleData.length > 1 
      ? (allCandleData[1].time - allCandleData[0].time) 
      : 3600; // Default to 1 hour
    
    const thresholdTime = loadMoreThreshold * averageCandleTime;
    
    // If user scrolled close to the beginning, load more data
    if (timeDifference <= thresholdTime) {
      setIsLoadingMore(true);
      
      try {
        // Request more historical data before the earliest timestamp
        const moreData = await onLoadMoreData(earliestDataTime);
        
        if (moreData && moreData.candles && moreData.candles.length > 0) {
          // Process and prepend the new data
          const newCandleData = moreData.candles.map(candle => {
            const timestamp = candle.timestamp ? 
              new Date(candle.timestamp).getTime() / 1000 : 
              new Date(candle.time).getTime() / 1000;
            
            return {
              time: timestamp,
              open: parseFloat(candle.open),
              high: parseFloat(candle.high),
              low: parseFloat(candle.low),
              close: parseFloat(candle.close),
            };
          }).sort((a, b) => a.time - b.time);

          // Filter out duplicates and merge with existing data
          const combinedData = [...newCandleData, ...allCandleData];
          const uniqueData = combinedData.filter((candle, index, array) => 
            index === 0 || candle.time !== array[index - 1].time
          );

          setAllCandleData(uniqueData);

          // Process volume data if available
          if (moreData.candles[0]?.volume !== undefined) {
            const newVolumeData = moreData.candles.map((candle) => {
              const timestamp = candle.timestamp ? 
                new Date(candle.timestamp).getTime() / 1000 : 
                new Date(candle.time).getTime() / 1000;
              
              const candleItem = newCandleData.find(c => c.time === timestamp);
              const color = candleItem && candleItem.close >= candleItem.open ? 
                'rgba(38, 166, 154, 0.5)' : 'rgba(239, 83, 80, 0.5)';
              
              return {
                time: timestamp,
                value: parseFloat(candle.volume || 0),
                color: color,
              };
            }).sort((a, b) => a.time - b.time);

            const combinedVolumeData = [...newVolumeData, ...allVolumeData];
            const uniqueVolumeData = combinedVolumeData.filter((candle, index, array) => 
              index === 0 || candle.time !== array[index - 1].time
            );

            setAllVolumeData(uniqueVolumeData);
          }

          if (DEBUG) {
            console.log(`Loaded ${newCandleData.length} additional historical candles`);
          }
        }
      } catch (error) {
        console.error('Error loading more historical data:', error);
      } finally {
        setIsLoadingMore(false);
      }
    }
  }, [onLoadMoreData, isLoadingMore, allCandleData, allVolumeData, loadMoreThreshold]);

  return {
    allCandleData,
    allVolumeData,
    isLoadingMore,
    processMarketData,
    loadMoreHistoricalData
  };
};
